import React from 'react';
import '../styles/DaySky.css';

/*
 * The light theme's backdrop: cumulus drifting across a blue sky.
 *
 * The daytime counterpart to NightSky, and built on the same rules --
 * aria-hidden, pointer-events none, fixed behind the content, generated once
 * from a seeded source so the sky is the same on every load.
 *
 * Clouds sit in three depth bands. Distant ones are smaller, paler and slower;
 * near ones are larger, whiter and cross sooner. That parallax is what stops a
 * row of identical puffs reading as wallpaper.
 */

const VB_W = 1600;
const VB_H = 900;

const seeded = (seed) => {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
};

/*
 * One cumulus: a run of overlapping lobes on a flat base. Real cumulus are
 * heaped on top and cut off underneath, so the lobes rise from a common
 * baseline rather than being scattered around a centre.
 */
const puff = (random) => {
  const lobes = [];
  const count = 4 + Math.floor(random() * 3);
  let x = 0;
  for (let i = 0; i < count; i += 1) {
    const mid = 1 - Math.abs(i / (count - 1) - 0.5) * 2; // tallest in the middle
    const r = 13 + mid * 15 + random() * 6;
    x += i === 0 ? r : r * 0.72;
    lobes.push({ cx: +x.toFixed(1), cy: +(-r * (0.28 + mid * 0.24)).toFixed(1), r: +r.toFixed(1) });
  }
  const width = x + lobes[lobes.length - 1].r;
  return { lobes, width };
};

const build = () => {
  const random = seeded(20260914);
  // depth: [scale, opacity, seconds to cross, count]
  const bands = [
    [0.55, 0.5, 240, 3],
    [0.85, 0.72, 170, 3],
    [1.25, 0.92, 115, 2],
  ];
  const clouds = [];
  bands.forEach(([scale, opacity, dur, count], band) => {
    for (let i = 0; i < count; i += 1) {
      clouds.push({
        ...puff(random),
        scale,
        opacity,
        dur,
        band,
        y: +(60 + band * 120 + random() * 130).toFixed(0),
        delay: +(-(dur * (i / count + random() * 0.25))).toFixed(1),
      });
    }
  });
  return clouds;
};

const CLOUDS = build();

const DaySky = () => (
  <div className="day-sky" aria-hidden="true">
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid slice" focusable="false">
      <defs>
        {/* Lit from above, shaded underneath -- the same overhead light as everything else. */}
        <linearGradient id="ds-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="62%" stopColor="#f4f8fd" />
          <stop offset="100%" stopColor="#d3e0ee" />
        </linearGradient>
        {/* A high, thin haze so the top of the sky is deeper than the horizon. */}
        <linearGradient id="ds-haze" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8fb6d8" stopOpacity=".38" />
          <stop offset="100%" stopColor="#8fb6d8" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect x={0} y={0} width={VB_W} height={VB_H * 0.62} fill="url(#ds-haze)" />

      {CLOUDS.map((cloud, index) => (
        <g
          key={index}
          className={`ds-cloud ds-cloud--${cloud.band}`}
          style={{
            '--y': `${cloud.y}px`,
            '--scale': cloud.scale,
            '--dur': `${cloud.dur}s`,
            '--delay': `${cloud.delay}s`,
            '--o': cloud.opacity,
            '--w': `${cloud.width}px`,
          }}
        >
          {cloud.lobes.map((lobe, i) => (
            <circle key={i} cx={lobe.cx} cy={lobe.cy} r={lobe.r} />
          ))}
          {/* Flat base: cumulus are cut off underneath, not round. */}
          <rect x={0} y={-2} width={cloud.width} height={14} rx={7} />
        </g>
      ))}
    </svg>
  </div>
);

export default DaySky;
